import { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ChakraProvider, Box, Spinner, Center } from '@chakra-ui/react';
import theme from './theme';
import { AuthProvider } from './services/context/AuthContext';
import ProtectedRoute from './services/context/ProtectedRoute';

// Public pages
import LoginPage from './pages/LoginPage';

// Public marketing pages (existing — NOT TOUCHED)
import ScrollProgress from './components/ScrollProgress';
import Navbar from './components/Navbar';
import ContactModal from './components/ContactModal';
import HomePage from './pages/HomePage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import Footer from './components/Footer';
import { useDisclosure } from '@chakra-ui/react';

// Dashboard shells
const AdminDashboard  = lazy(() => import('./pages/admin/AdminDashboard'));
const AgentDashboard  = lazy(() => import('./pages/agent/AgentDashboard'));

// Admin pages
const AdminOverview       = lazy(() => import('./pages/admin/pages/AdminOverview'));
const ManageAgents        = lazy(() => import('./pages/admin/pages/ManageAgents'));
const UploadPool          = lazy(() => import('./pages/admin/pages/UploadPool'));
const AdminPool           = lazy(() => import('./pages/admin/pages/AdminPool'));
const AdminClaims         = lazy(() => import('./pages/admin/pages/AdminClaims'));
const AdminInquiries      = lazy(() => import('./pages/admin/pages/AdminInquiries'));
const AdminAgentRequests  = lazy(() => import('./pages/admin/pages/AdminAgentRequests'));
const AdminAgentPerformance = lazy(() => import('./pages/admin/pages/AdminAgentPerformance'));
const AdminSuccessLogs    = lazy(() => import('./pages/admin/pages/AdminSuccessLogs'));

// Agent pages
const AgentOverview   = lazy(() => import('./pages/agent/pages/AgentOverview'));
const MyClaims        = lazy(() => import('./pages/agent/pages/MyClaims'));
const MyPerformance   = lazy(() => import('./pages/agent/pages/MyPerformance'));

function Loader() {
  return (
    <Center minH="100vh" bg="brand.black">
      <Spinner color="brand.coral" size="xl" thickness="3px" />
    </Center>
  );
}

function MarketingSite() {
  const { isOpen, onOpen, onClose } = useDisclosure();
  return (
    <>
      <ScrollProgress />
      <Navbar onOpenModal={onOpen} />
      <main>
        <HomePage onOpenModal={onOpen} />
        <AboutPage onOpenModal={onOpen} />
        <ContactPage onOpenModal={onOpen} />
      </main>
      <Footer />
      <ContactModal isOpen={isOpen} onClose={onClose} />
    </>
  );
}

export default function App() {
  return (
    <ChakraProvider theme={theme}>
      <Router>
        <AuthProvider>
          <Suspense fallback={<Loader />}>
            <Routes>
              {/* Public marketing site — UNTOUCHED */}
              <Route path="/" element={<MarketingSite />} />

              {/* Login */}
              <Route path="/login" element={<LoginPage />} />

              {/* Admin dashboard */}
              <Route path="/admin" element={
                <ProtectedRoute role="ADMIN"><AdminDashboard /></ProtectedRoute>
              }>
                <Route index element={<AdminOverview />} />
                <Route path="agents"             element={<ManageAgents />} />
                <Route path="upload"             element={<UploadPool />} />
                <Route path="pool"               element={<AdminPool />} />
                <Route path="claims"             element={<AdminClaims />} />
                <Route path="agent-performance"  element={<AdminAgentPerformance />} />
                <Route path="success-logs"       element={<AdminSuccessLogs />} />
                <Route path="inquiries"          element={<AdminInquiries />} />
                <Route path="agent-requests"     element={<AdminAgentRequests />} />
              </Route>

              {/* Agent dashboard */}
              <Route path="/agent" element={
                <ProtectedRoute role="AGENT"><AgentDashboard /></ProtectedRoute>
              }>
                <Route index element={<AgentOverview />} />
                <Route path="my-claims"      element={<MyClaims />} />
                <Route path="my-performance" element={<MyPerformance />} />
              </Route>

              {/* Fallback */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </AuthProvider>
      </Router>
    </ChakraProvider>
  );
}
